import { axiosInstance } from "@/lib/axios-instance";
import { AxiosError } from "axios";

/** What the intake form sends. Optional fields are omitted when left blank. */
export interface EcosystemSubmissionPayload {
  ecosystem: string;
  name: string;
  player_type: string;
  description: string;
  website: string;
  city: string;
  region: string;
  country?: string;
  funding_model?: string;
  funding_detail?: string;
  ownership?: string;
  /** GICS sub-industry codes (10-digit). The 8-digit parents are derived server-side. */
  gics_sub_industry?: string[];
  sector_focus?: string;
  tags?: string[];
  linkedin?: string;
  source_url?: string;
  hq_address?: {
    street?: string;
    line2?: string;
    city?: string;
    region?: string;
    postal_code?: string;
    country?: string;
  };
  submitter_name: string;
  submitter_email: string;
  submitter_role?: string;
}

export interface EcosystemSubmissionResult {
  _id: string;
  slug: string;
  ecosystem: string;
  status: string;
}

/** One thing the API says is wrong, keyed by the field it belongs to. */
export interface EcosystemFieldError {
  field: string;
  message: string;
}

/**
 * A rejected submission, carrying enough to render the failure where it
 * happened: `fieldErrors` for a 400 the form can annotate inline, `message` for
 * everything else (409 already-submitted, 429 rate limited, 5xx).
 */
export class EcosystemSubmissionError extends Error {
  readonly status: number;
  readonly fieldErrors: EcosystemFieldError[];

  constructor(
    message: string,
    status: number,
    fieldErrors: EcosystemFieldError[] = []
  ) {
    super(message);
    this.name = "EcosystemSubmissionError";
    this.status = status;
    this.fieldErrors = fieldErrors;
  }
}

export const ecosystemApi = {
  /**
   * Files one organization for review on an ecosystem map.
   *
   * Public — no auth header is required, and the endpoint is rate limited per
   * IP, which is what a 429 here means.
   */
  submit: async (
    payload: EcosystemSubmissionPayload
  ): Promise<EcosystemSubmissionResult> => {
    try {
      const response = await axiosInstance.post("/ecosystem_submission", payload);
      const result = response.data?.result;
      if (!result?.slug) {
        throw new EcosystemSubmissionError(
          "The submission did not come back confirmed. Please try again.",
          response.status
        );
      }
      return result as EcosystemSubmissionResult;
    } catch (error) {
      if (error instanceof EcosystemSubmissionError) throw error;

      const axiosError = error as AxiosError<{
        message?: string;
        errors?: EcosystemFieldError[];
      }>;
      const status = axiosError.response?.status ?? 0;
      const data = axiosError.response?.data;

      if (status === 429) {
        throw new EcosystemSubmissionError(
          data?.message ??
            "Too many submissions from this network. Please try again later.",
          status
        );
      }
      // A network failure has no response at all — say so rather than showing
      // the raw axios message, which reads as a bug in the page.
      if (!axiosError.response) {
        throw new EcosystemSubmissionError(
          "Could not reach StartupFuel. Check your connection and try again.",
          0
        );
      }

      throw new EcosystemSubmissionError(
        data?.message ?? "Could not save your submission. Please try again.",
        status,
        Array.isArray(data?.errors) ? data.errors : []
      );
    }
  },
};
